import { forwardRef, Ref, useCallback, useImperativeHandle, useMemo } from 'react';
import { deassociateSingleDeviceAPI } from '@/api/storeManagement/device';
import { useAppContext } from '@/provider/contexts';
import { MobileFeatureHandle, MobileFeatureViewProps } from '@/mobile/types';
import { createEslInputHandler } from '@/mobile/utils/createEslInputHandler';
import { createOnScanHandler } from '@/mobile/utils/createOnScanHandler';
import { useMobileState } from '@/mobile/provider/context';

const DeassociationView = forwardRef(({ dependency }: MobileFeatureViewProps, ref: Ref<MobileFeatureHandle>) => {
    const { setNotification } = useAppContext();
    const { state } = useMobileState();
    const {
        refs: { isSendingRef, eslMacInputRef },
        method: { focusCommonEslInput, setESL, setButtonInfo, addHistoryItem },
    } = dependency;

    const resetDeAssociation = useCallback(() => {
        setESL('');
    }, []);

    const handleDeAssociation = useCallback(async (mac: string) => {
        isSendingRef.current = true;
        setButtonInfo({ type: 'sending' });

        try {
            const response = await deassociateSingleDeviceAPI(mac);
            const statusCode = response?.data?.status;

            if (statusCode === 200) {
                setButtonInfo({ type: 'success' });
                addHistoryItem({
                    eslMac: mac,
                    status: 'success',
                    timestamp: new Date().toLocaleString(),
                    productName: 'Deassociated Successfully',
                    allSuccess: true,
                });
                return;
            }

            const errorReason =
                response?.data?.message?.trim() ||
                response?.data?.data?.push_information?.failed_macs?.[0]?.reason ||
                'Failed Deassociation';

            setButtonInfo({ type: 'error', msg: errorReason });
            addHistoryItem({
                eslMac: mac,
                status: 'failed',
                timestamp: new Date().toLocaleString(),
                productName: 'Failed Deassociation',
                errorReason,
                allSuccess: false,
            });
        } catch (error) {
            const errorReason = (error as Error).message || 'Unknown error occurred';
            setNotification({ message: errorReason, severity: 'error' });
            setButtonInfo({ type: 'error', msg: errorReason });
            addHistoryItem({
                eslMac: mac,
                status: 'failed',
                timestamp: new Date().toLocaleString(),
                productName: 'Failed Deassociation',
                errorReason,
                allSuccess: false,
            });
        } finally {
            isSendingRef.current = false;
            resetDeAssociation();
        }
    }, []);

    const onEslEnter = useMemo(
        () =>
            createEslInputHandler({
                isSubmitting: state.isSubmitting,
                setESL,
                setButtonInfo,
                eslMacInputRef,
                onValid: handleDeAssociation,
            }),
        [state.isSubmitting],
    );

    const onScan = useMemo(
        () =>
            createOnScanHandler({
                shouldProcess: () => !state.isSubmitting,
                onNormalizedScan: onEslEnter,
            }),
        [state.isSubmitting, onEslEnter],
    );

    const onModeChange = useCallback(() => {
        focusCommonEslInput();
    }, []);

    // Implement methods that parent use.
    useImperativeHandle(
        ref,
        () => ({
            onEslEnter,
            onScan,
            onModeChange,
        }),
        [onEslEnter, onScan, onModeChange],
    );

    return null;
});

export default DeassociationView;
